import React, { useState, useEffect } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

// Importaciones de tus imágenes
import PantallaImg from "../../static/media/Pantalla.webp";
import ImpresoraImg from "../../static/media/Impresora.webp";
import PlugPlayImg from "../../static/media/PlugPlay.webp";

const MotionBox = motion(Box);

// Paleta del proyecto
const colors = {
  primary: "#0D5DB5",
  primaryLight: "#0EA5E9",
  primaryDark: "#1A2B4A",
  textPrimary: "#1A2B4A",
  textSecondary: "#374151",
  textMuted: "#6B7280",
  background: "#FFFFFF",
  surface: "#F4F7FB",
};

export default function PosSection() {
  const theme = useTheme();
  // md en MUI es típicamente 900px, por debajo de esto se mostrará el slider
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeIndex, setActiveIndex] = useState(0);

  // Auto-play del carrusel en móvil (cambia cada 5 segundos)
  useEffect(() => {
    if (!isMobile) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev === 2 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [isMobile]);

  // Manejo de la interacción táctil (Swipe)
  const handleDragEnd = (e, { offset }) => {
    const swipe = offset.x;
    if (swipe < -40) {
      // Deslizar izquierda (Siguiente)
      setActiveIndex((prev) => (prev === 2 ? 0 : prev + 1));
    } else if (swipe > 40) {
      // Deslizar derecha (Anterior)
      setActiveIndex((prev) => (prev === 0 ? 2 : prev - 1));
    }
  };

  // TARJETA 1: PANTALLAS
  const renderCard1 = () => (
    <MotionBox
      initial={isMobile ? false : { opacity: 0, y: 20 }}
      whileInView={isMobile ? undefined : { opacity: 1, y: 0 }}
      viewport={isMobile ? undefined : { once: true, margin: "-50px" }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      whileHover={
        isMobile
          ? undefined
          : { y: -4, boxShadow: "0 24px 48px rgba(13,93,181,0.12)" }
      }
      sx={{
        gridColumn: { xs: "span 1", md: "span 2" },
        bgcolor: colors.background,
        borderRadius: "28px",
        border: `1px solid ${colors.primary}15`,
        overflow: "hidden",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        height: { xs: 480, sm: 500, md: "100%" }, // Altura fija en móvil para evitar saltos
        transition: "all 0.3s ease",
        "&:hover .bento-img": { transform: isMobile ? "none" : "scale(1.04)" },
      }}
    >
      <Box
        sx={{
          p: { xs: 3, md: 4 },
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          zIndex: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "1.4rem", md: "1.6rem" },
            fontWeight: 800,
            color: colors.textPrimary,
            mb: 1.5,
            lineHeight: 1.2,
          }}
        >
          Interfaz optimizada <br /> para pantallas
        </Typography>
        <Typography
          sx={{
            color: colors.textSecondary,
            fontSize: "0.95rem",
            lineHeight: 1.5,
          }}
        >
          Diseño visual intuitivo que permite a tus cajeros registrar productos
          y cobrar en segundos. Ideal para monitores táctiles o setups de
          escritorio completos.
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          p: { xs: 2, md: 3 },
          pl: { md: 0 },
          display: "flex",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            borderRadius: "20px",
            overflow: "hidden",
            position: "relative",
            bgcolor: `${colors.primary}05`,
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          }}
        >
          <Box
            className="bento-img"
            sx={{
              width: "100%",
              height: "100%",
              backgroundImage: `url(${PantallaImg})`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              transition: "transform 0.6s cubic-bezier(0.25, 1, 0.5, 1)",
            }}
          />
        </Box>
      </Box>
    </MotionBox>
  );

  // TARJETA 2: IMPRESORAS
  const renderCard2 = () => (
    <MotionBox
      initial={isMobile ? false : { opacity: 0, y: 20 }}
      whileInView={isMobile ? undefined : { opacity: 1, y: 0 }}
      viewport={isMobile ? undefined : { once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: isMobile ? 0 : 0.1, ease: "easeOut" }}
      whileHover={
        isMobile
          ? undefined
          : { y: -4, boxShadow: "0 24px 48px rgba(13,93,181,0.12)" }
      }
      sx={{
        gridColumn: "span 1",
        bgcolor: colors.background,
        borderRadius: "28px",
        border: `1px solid ${colors.primary}15`,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: { xs: 480, sm: 500, md: "100%" },
        transition: "all 0.3s ease",
        "&:hover .bento-img": { transform: isMobile ? "none" : "scale(1.05)" },
      }}
    >
      <Box sx={{ p: { xs: 3, md: 4 }, pb: { xs: 1, md: 1 }, zIndex: 2 }}>
        <Typography
          sx={{
            fontSize: { xs: "1.4rem", md: "1.5rem" },
            fontWeight: 800,
            color: colors.textPrimary,
            mb: 1,
            lineHeight: 1.2,
          }}
        >
          Recibos al instante
        </Typography>
        <Typography
          sx={{
            color: colors.textSecondary,
            fontSize: "0.95rem",
            lineHeight: 1.5,
          }}
        >
          Impresión automática. Compatible con térmicas de 58 y 80mm.
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          p: { xs: 2, md: 3 },
          pt: { xs: 1, md: 2 },
          display: "flex",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            borderRadius: "20px",
            overflow: "hidden",
            position: "relative",
            bgcolor: `${colors.primary}05`,
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          }}
        >
          <Box
            className="bento-img"
            sx={{
              width: "100%",
              height: "100%",
              backgroundImage: `url(${ImpresoraImg})`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              transition: "transform 0.6s cubic-bezier(0.25, 1, 0.5, 1)",
            }}
          />
        </Box>
      </Box>
    </MotionBox>
  );

  // TARJETA 3: INSTALACIÓN PERSONALIZADA
  const renderCard3 = () => (
    <MotionBox
      initial={isMobile ? false : { opacity: 0, y: 20 }}
      whileInView={isMobile ? undefined : { opacity: 1, y: 0 }}
      viewport={isMobile ? undefined : { once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: isMobile ? 0 : 0.2, ease: "easeOut" }}
      whileHover={
        isMobile
          ? undefined
          : { y: -4, boxShadow: "0 24px 48px rgba(13,93,181,0.25)" }
      }
      sx={{
        gridColumn: { xs: "span 1", md: "span 3" },
        bgcolor: colors.primaryDark,
        borderRadius: "28px",
        overflow: "hidden",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "stretch",
        color: "#fff",
        height: { xs: 480, sm: 500, md: "100%" },
        transition: "all 0.3s ease",
        "&:hover .bento-img": { transform: isMobile ? "none" : "scale(1.03)" },
      }}
    >
      <Box
        sx={{
          p: { xs: 3, md: 4 },
          flex: 1,
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "1.4rem", md: "1.6rem" },
            fontWeight: 800,
            mb: 1.5,
            lineHeight: 1.2,
          }}
        >
          Instalación Personalizada
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.7)",
            fontSize: "0.95rem",
            lineHeight: 1.5,
            maxWidth: 600,
          }}
        >
          Adaptamos y configuramos el sistema según las necesidades específicas
          de tu negocio. Dejamos tus lectores, básculas y gavetas listos para
          operar desde el primer momento, garantizando un inicio sin
          complicaciones.
        </Typography>
        {/* Box con mb: 10 eliminado para evitar desbordes */}
      </Box>

      <Box
        sx={{
          flex: 1,
          p: { xs: 2, md: 4 },
          pl: { md: 0 },
          display: "flex",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            borderRadius: "20px",
            overflow: "hidden",
            position: "relative",
            bgcolor: "rgba(255,255,255,0.06)",
            boxShadow:
              "inset 0 0 0 1px rgba(255,255,255,0.1), 0 8px 30px rgba(0,0,0,0.2)",
          }}
        >
          <Box
            className="bento-img"
            sx={{
              width: "100%",
              height: "100%",
              backgroundImage: `url(${PlugPlayImg})`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              transition: "transform 0.6s cubic-bezier(0.25, 1, 0.5, 1)",
            }}
          />
        </Box>
      </Box>
    </MotionBox>
  );

  const cards = [renderCard1(), renderCard2(), renderCard3()];

  return (
    <Box
      id="punto-de-venta"
      sx={{
        width: "100%",
        py: { xs: 5, md: 6 }, // Padding vertical más compacto
        px: { xs: 3, sm: 5, md: 6, lg: 8 },
        bgcolor: colors.surface,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decoración de fondo */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          left: "-5%",
          width: 500,
          height: 500,
          background: `radial-gradient(circle, ${colors.primaryLight}08 0%, transparent 70%)`,
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{ maxWidth: "1200px", mx: "auto", position: "relative", zIndex: 1 }}
      >
        {/* Encabezado */}
        <MotionBox
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          sx={{
            mb: { xs: 4, md: 5 },
            textAlign: "center",
            maxWidth: 700,
            mx: "auto",
          }} // Margin inferior reducido
        >
          <Typography
            component="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "2rem", md: "2.6rem" }, // Texto un poco más ajustado
              color: colors.textPrimary,
              lineHeight: 1.15,
              mb: 1.5,
            }}
          >
            Punto de venta listo para{" "}
            <Box
              component="span"
              sx={{
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              despachar
            </Box>
          </Typography>
          <Typography
            sx={{
              color: colors.textSecondary,
              fontSize: { xs: "0.95rem", md: "1.05rem" },
              lineHeight: 1.5,
            }}
          >
            Sincroniza tus equipos, pantallas e impresoras. Crea una experiencia
            de cobro rápida, profesional y sin demoras para tus clientes.
          </Typography>
        </MotionBox>

        {/* CONTENIDO PRINCIPAL: Slider en Móvil / Grid en Desktop */}
        {isMobile ? (
          <Box sx={{ position: "relative", overflow: "visible", px: 1, pb: 2 }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                onDragEnd={handleDragEnd}
                style={{ touchAction: "pan-y", cursor: "grab" }}
              >
                {cards[activeIndex]}
              </motion.div>
            </AnimatePresence>

            {/* Puntos Indicadores del Slider */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
                gap: 1.5,
              }}
            >
              {cards.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  sx={{
                    width: activeIndex === index ? 24 : 10,
                    height: 10,
                    borderRadius: "10px",
                    bgcolor:
                      activeIndex === index ? colors.primary : colors.textMuted,
                    opacity: activeIndex === index ? 1 : 0.3,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              // Definimos el alto exacto de las dos filas para que no se extienda hacia abajo
              gridTemplateRows: "minmax(260px, 1fr) minmax(240px, 1fr)",
              gap: 3,
            }}
          >
            {cards}
          </Box>
        )}
      </Box>
    </Box>
  );
}
